import { useState } from "react";
import { GenieAcsService } from "../../../../../services/GenieAcsService";
import { ModalWraperGenieAcs } from "./wraper";
import { FaTag, FaXmark } from "react-icons/fa6";

// Regex sesuai backend: hanya huruf, angka, underscore, dan dash
const TAG_REGEX = /^[a-zA-Z0-9_-]+$/;

export const GenieAcsAddDeviceTag = ({
    deviceId,
    currentTags = [],
    refresh,
    openModal,
    setOpenModal,
}) => {
    const [loading, setLoading] = useState(false);
    const [tagInput, setTagInput] = useState("");
    const [error, setError] = useState("");

    const closeModal = () => {
        setOpenModal(false);
        setTagInput("");
        setError("");
        setLoading(false);
    };

    const handleAddTag = async (e) => {
        e.preventDefault();

        const trimmedTag = tagInput.trim();

        // Validasi kosong
        if (!trimmedTag) {
            setError("Nama tag tidak boleh kosong.");
            return;
        }

        // Validasi panjang (sesuai backend max:64)
        if (trimmedTag.length > 64) {
            setError("Nama tag maksimal 64 karakter.");
            return;
        }

        // Validasi karakter sesuai backend regex: ^[a-zA-Z0-9_-]+$         
        if (!TAG_REGEX.test(trimmedTag)) {
            setError("Tag hanya boleh mengandung huruf, angka, underscore (_), dan dash (-).");
            return;
        }

        // Validasi duplikat dari tag yang sudah ada (case-insensitive)
        if (currentTags.some((t) => t.toLowerCase() === trimmedTag.toLowerCase())) {
            setError("Tag ini sudah ada pada device.");
            return;
        }

        try {
            setLoading(true);
            setError("");

            // ✅ Sesuaikan payload dengan backend: array of tags
            await GenieAcsService.addDeviceTag({
                data: {
                    device_id: deviceId,
                    tags: [trimmedTag],
                },
            });

            if (refresh) refresh();
            closeModal();
        } catch (err) {
            console.error("Gagal menambahkan tag:", err);
            const message = err?.response?.data?.message || "Gagal menambahkan tag.";
            setError(message);
        } finally {
            setLoading(false);
        }
    };


    const handleRemoveTag = async (tagToRemove) => {
        if (!window.confirm(`Hapus tag "${tagToRemove}"?`)) return;

        try {
            setError("");

            // ✅ Sesuaikan payload delete dengan backend: array of tags
            await GenieAcsService.deleteDeviceTag({
                data: {
                    device_id: deviceId,
                    tags: [tagToRemove],
                },
            });

            if (refresh) refresh();
        } catch (err) {
            console.error("Gagal menghapus tag:", err);
            const message = err?.response?.data?.message || "Gagal menghapus tag.";
            setError(message);
        }
    };

    // Cek validasi real-time untuk styling input
    const isInputValid = tagInput.trim() === "" || TAG_REGEX.test(tagInput.trim());

    return (
        <ModalWraperGenieAcs
            isOpen={openModal}
            setIsOpen={setOpenModal}
            title="Kelola Tag Device"
        >
            <form onSubmit={handleAddTag} className="space-y-5">
                {/* Daftar Tag Saat Ini */}
                {currentTags.length > 0 && (
                    <div>
                        <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">
                            Tag Saat Ini
                        </label>

                        <div className="flex flex-wrap gap-2 p-3 rounded-xl bg-slate-800/40 border border-slate-700/30 min-h-[48px]">
                            {currentTags.map((tag, idx) => (
                                <span
                                    key={idx}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-cyan-500/10 text-cyan-300 border border-cyan-500/20 group"
                                >
                                    <FaTag className="text-[10px]" />
                                    {tag}

                                    <button
                                        type="button"
                                        onClick={() => handleRemoveTag(tag)}
                                        className="ml-0.5 p-0.5 rounded-full text-cyan-500/60 hover:text-red-400 hover:bg-red-400/10 transition-colors"
                                        title={`Hapus tag "${tag}"`}
                                    >
                                        <FaXmark className="text-[10px]" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Input Tag Baru */}
                <div>
                    <label
                        htmlFor="tag_input"
                        className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider"
                    >
                        Tambah Tag Baru
                    </label>

                    <div className="flex gap-2">
                        <input
                            id="tag_input"
                            type="text"
                            value={tagInput}
                            onChange={(e) => {
                                const value = e.target.value;
                                // Filter karakter yang tidak diizinkan saat mengetik
                                const filtered = value.replace(/[^a-zA-Z0-9_-]/g, "");
                                setTagInput(filtered);
                                if (error) setError("");
                            }}
                            className={`flex-1 px-4 py-3 text-sm rounded-xl bg-slate-800/60 border ${!isInputValid
                                    ? "border-red-500/50 focus:border-red-500"
                                    : "border-slate-700 focus:border-cyan-500"
                                } text-slate-100 placeholder-slate-500 focus:outline-none transition-colors`}
                            placeholder="Contoh: Pelanggan-Ahmad_01"
                            maxLength={64}
                            autoFocus
                        />
                    </div>

                    {/* Character Counter & Hint */}
                    <div className="flex justify-between mt-1.5">
                        <p className="text-[11px] text-slate-500">
                            Huruf, angka, underscore (_), dan dash (-) saja.
                        </p>
                        <p className={`text-[11px] ${tagInput.length > 56 ? "text-amber-400" : "text-slate-600"}`}>
                            {tagInput.length}/64
                        </p>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="flex items-start gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20">
                        <span className="text-red-400 text-xs leading-relaxed">{error}</span>
                    </div>
                )}

                {/* Device ID Info */}
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-slate-800/30 border border-slate-700/20">
                    <span className="text-[11px] text-slate-500">Device:</span>
                    <span className="text-[11px] font-mono text-slate-400 break-all">{deviceId}</span>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-1">
                    <button
                        type="button"
                        onClick={closeModal}
                        className="flex-1 py-3 rounded-xl border border-slate-700 text-slate-300 text-sm font-medium transition-colors hover:bg-slate-800"
                    >
                        Tutup
                    </button>

                    <button
                        type="submit"
                        disabled={loading || !tagInput.trim() || !isInputValid}
                        className="flex-1 py-3 rounded-xl bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-700 disabled:text-slate-500 text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
                    >
                        {loading ? (
                            <span className="flex items-center gap-2">
                                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                Menyimpan...
                            </span>
                        ) : (
                            <>
                                <FaTag className="text-xs" />
                                Tambah Tag
                            </>
                        )}
                    </button>
                </div>
            </form>
        </ModalWraperGenieAcs>
    );
};